import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { useThemes } from '@hooks/useThemes';
import styles from './RangeFilter.module.css';

export interface RangeFilterProps {
    maxInput: number;
    minValue: number;
    maxValue: number;
    step: number;
    labelSuffix?: '€';
    setMinValue: (value: number) => void;
    setMaxValue: (value: number) => void;
}

export const RangeFilter: FC<RangeFilterProps> = ({
    maxInput,
    minValue,
    maxValue,
    step,
    labelSuffix,
    setMinValue,
    setMaxValue,
}) => {
    const { useGrayscaleTheme } = useThemes();
    const { t } = useTranslation();

    const translations = {
        rangeFilterTitle: t('range-filter-component.title'),
    };

    const [labelRangeMin, setLabelRangeMin] = useState(minValue);
    const [labelRangeMax, setLabelRangeMax] = useState(maxValue);

    const labelMinRef = useRef<HTMLLabelElement>(null);
    const labelMaxRef = useRef<HTMLLabelElement>(null);

    const progressRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        renderRangeInput(null, minValue, maxValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        renderRangeInput('min', minValue, maxValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minValue]);

    useEffect(() => {
        renderRangeInput('max', minValue, maxValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxValue]);

    function snapToStep(value: number) {
        // round to nearest multiple of `step`, but clamp to [0, maxInput]
        const snapped = Math.round(value / step) * step;
        return Math.min(Math.max(snapped, 0), maxInput);
    }

    function handleChangeMin(e: ChangeEvent<HTMLInputElement>) {
        let value = snapToStep(Number(e.target.value));
        if (value <= maxValue - step) {
            setMinValue(value);
        }
    }

    function handleChangeMax(e: ChangeEvent<HTMLInputElement>) {
        let value = snapToStep(Number(e.target.value));
        if (value >= minValue + step) {
            setMaxValue(value);
        }
    }

    function renderRangeInput(currentChangedInput: string | null, inputRangeMin: number, inputRangeMax: number) {
        if (inputRangeMax - inputRangeMin < step) {
            // prevent min and max slider's thumbs from switching sides
            if (currentChangedInput) {
                if (currentChangedInput === 'min') {
                    // min 0
                    const minValue = Math.max(inputRangeMax - step, 0);
                    setMinValue(minValue);
                } else {
                    // max is the max available value
                    const maxValue = Math.min(inputRangeMax + step, maxInput);
                    setMaxValue(maxValue);
                }
            }
        }

        const leftSpace = (inputRangeMin / maxInput) * 100 + '%';
        const rightSpace = 100 - (inputRangeMax / maxInput) * 100 + '%';

        // labels
        // the positioning of the labels will also have in count it's width, to ensure that they are centered
        // with the slider's thumbs
        // label with min value
        setLabelRangeMin(inputRangeMin);
        if (labelMinRef.current !== null) {
            const rangeMinLabelHalfWidth = Math.round(labelMinRef.current.clientWidth / 2) + 'px';
            labelMinRef.current.style.left = 'calc(' + leftSpace + ' - ' + rangeMinLabelHalfWidth + ')';
        }

        // label with max value
        setLabelRangeMax(inputRangeMax);
        if (labelMaxRef.current !== null) {
            const rangeMaxLabelHalfWidth = Math.round(labelMaxRef.current.clientWidth / 2) + 'px';
            labelMaxRef.current.style.right = 'calc(' + rightSpace + ' - ' + rangeMaxLabelHalfWidth + ')';
        }

        // colored bar
        if (progressRef.current !== null) {
            progressRef.current.style.left = leftSpace;
            progressRef.current.style.right = rightSpace;
        }
    }

    return (
        <div
            className={
                // themes
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ' pt-2'
            }>
            <Typography classes="mb-2" appearanceLevel={TypographyAppearanceVariants.level7}>
                {translations.rangeFilterTitle}
            </Typography>
            <hr />
            <div className={styles['price-range-container']}>
                <div className={styles['slider']}>
                    <Typography tagVariant={TypographyTagVariants.span}>
                        <>
                            <label className={styles['zero-label']}>0{labelSuffix || ''}</label>
                            <label className={styles['range-min-label']} ref={labelMinRef}>
                                {labelRangeMin + (labelSuffix || '')}
                            </label>
                            <label className={styles['range-max-label']} ref={labelMaxRef}>
                                {labelRangeMax + (labelSuffix || '')}
                            </label>
                        </>
                    </Typography>
                    <div className={styles['progress']} ref={progressRef}></div>
                </div>
                <div className={styles['range-input']}>
                    <input
                        type="range"
                        className="range-min"
                        name="range-min-value"
                        min={0}
                        max={maxInput}
                        value={minValue}
                        step={0.01}
                        onChange={(e) => handleChangeMin(e)}
                    />
                    <input
                        type="range"
                        className="range-max"
                        name="range-max-value"
                        min={0}
                        max={maxInput}
                        value={maxValue}
                        step={0.01}
                        onChange={(e) => handleChangeMax(e)}
                    />
                </div>
            </div>
        </div>
    );
};
